<script setup lang="ts">
import {ChevronLeft, ChevronRight} from "@lucide/vue";
import MedicationCheckupPrescriptionPage from "@/medication-checkup/components/MedicationCheckupPrescriptionPage.vue";
import {TreatmentSheet} from "@/medication-checkup/services/TreatmentSheets";
import {useSheetCursor} from "@/medication-checkup/composables/useSheetCursor";

/**
 * The prescriptions of a line beside the form its posology is entered on:
 * the most recent first — the one the posology was read on — the others a
 * click away.
 */

const props = defineProps<{
    sheets: TreatmentSheet[];
}>();

const {index, sheet, go} = useSheetCursor(() => props.sheets);
</script>

<template>
  <div class="flex min-h-0 flex-col gap-2">
    <div class="flex items-center gap-2 text-[12px]">
      <span class="flex min-w-0 flex-1 flex-col">
        <span class="truncate font-semibold text-gray-dark">{{ sheet?.prescriber ?? 'Prescripteur inconnu' }}</span>
        <span v-if="sheet?.deliveredAt" class="text-[11px] text-muted-foreground">
          Délivrée le {{ $filters.date(sheet.deliveredAt, 'dayOnly') }}
        </span>
      </span>
      <span v-if="sheets.length > 1" class="flex shrink-0 items-center gap-1">
        <button
          type="button"
          class="flex size-6 items-center justify-center rounded text-slate-400 hover:text-slate-700 disabled:opacity-30"
          :disabled="index === 0"
          title="Ordonnance plus récente"
          @click="go(-1)"
        >
          <ChevronLeft class="size-4" />
        </button>
        <span class="w-9 text-center text-[11px] tabular-nums text-muted-foreground">{{ index + 1 }} / {{ sheets.length }}</span>
        <button
          type="button"
          class="flex size-6 items-center justify-center rounded text-slate-400 hover:text-slate-700 disabled:opacity-30"
          :disabled="index === sheets.length - 1"
          title="Ordonnance plus ancienne"
          @click="go(1)"
        >
          <ChevronRight class="size-4" />
        </button>
      </span>
    </div>

    <div class="min-h-0 flex-1 overflow-hidden rounded-[3px] bg-white shadow-[0_3px_14px_rgba(19,45,90,.16)]">
      <MedicationCheckupPrescriptionPage
        v-if="sheet?.prescriptionKey"
        :key="sheet.prescriptionKey"
        :prescription-key="sheet.prescriptionKey"
      />
    </div>
  </div>
</template>
